import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import { registerErrorMessage, isOkCommand, parseResponse } from '@skynet/data-ws-core';
import type { TradingAddTaskState, TradingApiKeysInstruments, TradingApiKeysToken } from './types';
import { TradingAddTaskCommands } from './types';

import { describeWsFailure } from 'src/store/utils';
import { actionEsOnMessage } from '@skynet/data-ws-es';
import { listen } from '../actions';
import { addTask } from './thunks';

registerErrorMessage(TradingAddTaskCommands.AddTask, {
  title: 'Add Task Error',
  getDescription: describeWsFailure,
});

const allowedCommands = Object.values(TradingAddTaskCommands);

export const initialState: TradingAddTaskState = {
  error: null,
  tokens: null,
  filteredTokens: [],
  selectedToken: null,
  instruments: [],
  activeListenSection: null,
  isLoadingTickers: false,
  resAddTaskStatus: null,
  searchValue: '',
  requestId: '',
};

const transformTokens = (tokens: TradingApiKeysToken[]) => {
  if (!tokens?.length) return [];

  return tokens.flatMap((token) => {
    const [base] = token.ticker.split('/');
    return token.quotes.map((quote) => ({
      ...token,
      ticker: `${base}/${quote}`,
    }));
  });
};

const tradingAddTaskSlice = createSlice({
  name: 'tradingAddTask',
  initialState,
  reducers: {
    setActiveListenSection: (state, action: PayloadAction<string | null>) => {
      state.activeListenSection = action.payload;
    },

    setFilteredTokens: (state, action: PayloadAction<TradingApiKeysToken[]>) => {
      state.filteredTokens = action.payload;
    },

    setSelectedToken: (state, action: PayloadAction<TradingApiKeysToken | null>) => {
      state.selectedToken = action.payload;
    },
    setIsLoadingTickers: (state, action: PayloadAction<boolean>) => {
      state.isLoadingTickers = action.payload;
    },

    searchTokens: (state, action: PayloadAction<{ value: string }>) => {
      state.searchValue = action.payload.value;
      state.filteredTokens = [];
    },

    selectToken: (state, action: PayloadAction<{ value: string } | null>) => {
      if (!action.payload?.value || !state.tokens) {
        state.selectedToken = null;
        state.activeListenSection = null;
        return;
      }

      const [selectedBase, selectedQuote] = action.payload.value.split('/');

      const originalToken = state.tokens.find((token) => {
        const [base] = token.ticker.split('/');
        return base === selectedBase && token.quotes.includes(selectedQuote);
      });

      if (originalToken) {
        state.selectedToken = {
          ...originalToken,
          ticker: action.payload.value,
        };
        state.activeListenSection = action.payload.value.toLowerCase();
      }
    },

    clearSelection: (state) => {
      state.selectedToken = null;
      state.filteredTokens = [];
      state.activeListenSection = null;
      state.isLoadingTickers = false;
      state.searchValue = '';
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listen, (state, action) => {
      const { token, requestId } = action.payload;
      const { isInitCall } = action.meta;
      if (isInitCall && token) {
        state.isLoadingTickers = true;
      }
      if (requestId) {
        state.requestId = requestId;
      }
    });

    builder.addCase(addTask.pending, (state) => {
      state.resAddTaskStatus = 'pending';
    });

    builder.addCase(actionEsOnMessage, (state, action) => {
      const { json, skipProcessing, error, okCommand } = parseResponse(
        action.payload,
        allowedCommands,
      );

      if (skipProcessing || !json) return;

      const { command, errorCommand } = json;

      if (error) {
        if (errorCommand === TradingAddTaskCommands.AddTask) {
          state.resAddTaskStatus = 'error';
        }
        state.error = error;
        return;
      }

      if (isOkCommand(command) && okCommand === TradingAddTaskCommands.AddTask) {
        state.resAddTaskStatus = 'success';
        return;
      }

      if (command === TradingAddTaskCommands.Instruments) {
        const { instruments } = json as { instruments: TradingApiKeysInstruments[] };

        if (!Array.isArray(instruments)) {
          return;
        }

        state.instruments = instruments;
      }

      if (command === TradingAddTaskCommands.Token) {
        const token = json as TradingApiKeysToken;
        if (state.requestId !== token.requestId) {
          return;
        }
        const tokens = token.ticker ? [token] : [];

        state.tokens = tokens;
        const transformedTokens = transformTokens(tokens);
        state.filteredTokens = transformedTokens.filter((token) => {
          return token.ticker.toLowerCase().includes(state.searchValue?.toLowerCase());
        });

        if (transformedTokens.length > 0 || token.price === 0) {
          state.isLoadingTickers = false;
        }

        if (state.selectedToken) {
          const updatedToken = state.filteredTokens.find(
            (t) => t.ticker.toLowerCase() === state.selectedToken?.ticker.toLowerCase(),
          );
          if (updatedToken) {
            state.selectedToken = updatedToken;
          }
        }
      }

      state.resAddTaskStatus = null;
      state.isLoadingTickers = false;
    });
  },
});

export const tradingAddTaskActions = { ...tradingAddTaskSlice.actions, addTask };
export default tradingAddTaskSlice.reducer;
